import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, ArrowLeft, PawPrint } from "lucide-react";
import { api, mensajeError } from "../api/client";
import { Spinner } from "../components/ui/Spinner";

/**
 * Certificado de salud formal (tipo el que piden las aerolíneas al viajar
 * con una mascota). A propósito tiene un diseño distinto al resto del
 * portal público — hoja membretada blanca y formal, no la vista amigable
 * con acentos de color, para que se pueda imprimir y presentar como un
 * documento oficial del negocio.
 */

interface DatosCertificadoSalud {
  certificado: {
    fechaEmision: string;
    desparasitacion?: string | null;
    declaracionConfirmada: boolean;
  };
  veterinario: {
    nombre: string;
    titulo?: string | null;
    cedula?: string | null;
    telefono?: string | null;
    email?: string | null;
  };
  vacunas: {
    nombre: string;
    lote?: string | null;
    farmaceutica?: string | null;
    fechaAplicacion: string;
    fechaProximaDosis?: string | null;
  }[];
  mascota: {
    nombre: string;
    especie: string;
    raza?: string | null;
    sexo: string;
    fechaNacimiento?: string | null;
    color?: string | null;
    microchip?: string | null;
  };
  propietario: {
    nombre: string;
    direccion?: string | null;
  };
  negocio: {
    nombre: string;
    logoUrl?: string | null;
    colorPrimario?: string | null;
    direccion?: string | null;
    telefono?: string | null;
    email?: string | null;
    rfc?: string | null;
  };
}

function calcularEdad(fechaNacimiento: string): string {
  const nacimiento = new Date(fechaNacimiento);
  const ahora = new Date();
  let años = ahora.getFullYear() - nacimiento.getFullYear();
  let meses = ahora.getMonth() - nacimiento.getMonth();
  if (ahora.getDate() < nacimiento.getDate()) meses--;
  if (meses < 0) {
    años--;
    meses += 12;
  }
  const partes: string[] = [];
  if (años > 0) partes.push(`${años} año${años === 1 ? "" : "s"}`);
  if (meses > 0) partes.push(`${meses} mes${meses === 1 ? "" : "es"}`);
  return partes.join(", ") || "Menos de un mes";
}

export default function CertificadoSalud() {
  const { token, certificadoId } = useParams();
  const [datos, setDatos] = useState<DatosCertificadoSalud | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !certificadoId) return;
    api
      .get(`/portal-mascota/${token}/certificado-salud/${certificadoId}`)
      .then((res) => setDatos(res.data))
      .catch((err) => setError(mensajeError(err)));
  }, [token, certificadoId]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 text-center">
        <div className="max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <PawPrint size={32} className="mx-auto mb-3 text-slate-300" />
          <p className="text-slate-600">{error}</p>
          <Link to={`/portal-mascota/${token}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:underline">
            <ArrowLeft size={14} /> Volver al expediente
          </Link>
        </div>
      </div>
    );
  }

  if (!datos) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  const { certificado, veterinario, vacunas, mascota, propietario, negocio } = datos;
  const colorAcento = negocio.colorPrimario || "#0EA5A4";

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10 print:bg-white print:py-0">
      <style>{`
        @media print {
          .no-imprimir { display: none !important; }
          body { background: white !important; }
        }
      `}</style>

      <div className="mx-auto max-w-3xl">
        <div className="no-imprimir mb-4 flex items-center justify-between">
          <Link to={`/portal-mascota/${token}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700">
            <ArrowLeft size={14} /> Volver al expediente
          </Link>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-white shadow-sm"
            style={{ backgroundColor: colorAcento }}
          >
            <Printer size={15} /> Imprimir certificado
          </button>
        </div>

        {/* Hoja membretada */}
        <div className="bg-white p-10 shadow-sm print:shadow-none" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
          {/* Encabezado / membrete */}
          <div className="mb-8 flex items-center justify-between border-b-2 pb-5" style={{ borderColor: colorAcento }}>
            <div className="flex items-center gap-4">
              {negocio.logoUrl && (
                <img src={negocio.logoUrl} alt={negocio.nombre} className="h-16 w-16 rounded-lg object-cover" />
              )}
              <div>
                <p className="text-xl font-bold text-slate-800">{negocio.nombre}</p>
                {negocio.direccion && <p className="text-xs text-slate-500">{negocio.direccion}</p>}
                <p className="text-xs text-slate-500">
                  {[negocio.telefono, negocio.email].filter(Boolean).join(" · ")}
                </p>
                {negocio.rfc && <p className="text-xs text-slate-400">RFC: {negocio.rfc}</p>}
              </div>
            </div>
            <div className="text-right text-xs text-slate-400">
              <p>Folio: {certificadoId?.slice(0, 8).toUpperCase()}</p>
              <p>Emitido: {new Date(certificado.fechaEmision).toLocaleDateString("es-MX", { dateStyle: "long" })}</p>
            </div>
          </div>

          <h1 className="mb-6 text-center text-2xl font-bold uppercase tracking-wide text-slate-800">
            Certificado de salud animal
          </h1>

          {/* 1. Datos del propietario */}
          <section className="mb-6">
            <h2 className="mb-2 border-b border-slate-200 pb-1 text-sm font-bold uppercase tracking-wide text-slate-600">
              1. Datos del propietario
            </h2>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-700">
              <p><span className="text-slate-400">Nombre completo:</span> {propietario.nombre}</p>
              <p><span className="text-slate-400">Dirección:</span> {propietario.direccion || "No especificada"}</p>
            </div>
          </section>

          {/* 2. Datos de la mascota */}
          <section className="mb-6">
            <h2 className="mb-2 border-b border-slate-200 pb-1 text-sm font-bold uppercase tracking-wide text-slate-600">
              2. Datos del paciente
            </h2>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-700">
              <p><span className="text-slate-400">Nombre:</span> {mascota.nombre}</p>
              <p><span className="text-slate-400">Especie:</span> {mascota.especie}</p>
              <p><span className="text-slate-400">Raza:</span> {mascota.raza || "No especificada"}</p>
              <p><span className="text-slate-400">Sexo:</span> {mascota.sexo}</p>
              <p><span className="text-slate-400">Edad:</span> {mascota.fechaNacimiento ? calcularEdad(mascota.fechaNacimiento) : "No especificada"}</p>
              <p><span className="text-slate-400">Color de pelaje:</span> {mascota.color || "No especificado"}</p>
              <p className="col-span-2"><span className="text-slate-400">Microchip:</span> {mascota.microchip || "No aplica"}</p>
            </div>
          </section>

          {/* 3. Declaración de salud */}
          <section className="mb-6">
            <h2 className="mb-2 border-b border-slate-200 pb-1 text-sm font-bold uppercase tracking-wide text-slate-600">
              3. Declaración de estado de salud
            </h2>
            <p className="text-sm leading-relaxed text-slate-700">
              El suscrito, médico veterinario responsable, certifica bajo protesta de decir verdad que, tras haber
              examinado clínicamente al paciente descrito en este documento, se encuentra <strong>libre de
              enfermedades infectocontagiosas</strong>, <strong>libre de parásitos externos e internos</strong>,{" "}
              <strong>sin heridas recientes</strong> y en condiciones físicas <strong>aptas para viajar</strong>.
            </p>
          </section>

          {/* 4. Vacunación y desparasitación */}
          <section className="mb-6">
            <h2 className="mb-2 border-b border-slate-200 pb-1 text-sm font-bold uppercase tracking-wide text-slate-600">
              4. Constancia de vacunación y desparasitación
            </h2>
            {vacunas.length === 0 ? (
              <p className="text-sm text-slate-400">No hay vacunas registradas para incluir en este certificado.</p>
            ) : (
              <table className="w-full text-left text-sm text-slate-700">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase text-slate-400">
                    <th className="py-1 pr-2 font-medium">Vacuna</th>
                    <th className="py-1 pr-2 font-medium">Laboratorio</th>
                    <th className="py-1 pr-2 font-medium">Aplicada</th>
                    <th className="py-1 font-medium">Próxima dosis</th>
                  </tr>
                </thead>
                <tbody>
                  {vacunas.map((v, i) => (
                    <tr key={i} className="border-b border-slate-100 last:border-0">
                      <td className="py-1.5 pr-2">{v.nombre}</td>
                      <td className="py-1.5 pr-2">{v.farmaceutica || "—"}</td>
                      <td className="py-1.5 pr-2">{new Date(v.fechaAplicacion).toLocaleDateString("es-MX")}</td>
                      <td className="py-1.5">{v.fechaProximaDosis ? new Date(v.fechaProximaDosis).toLocaleDateString("es-MX") : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {certificado.desparasitacion && (
              <p className="mt-3 text-sm text-slate-700">
                <span className="text-slate-400">Desparasitación:</span> {certificado.desparasitacion}
              </p>
            )}
          </section>

          {/* Firma */}
          <section className="mt-14">
            <h2 className="mb-2 border-b border-slate-200 pb-1 text-sm font-bold uppercase tracking-wide text-slate-600">
              5. Responsable veterinario
            </h2>
            <div className="mt-10 flex flex-col items-center text-center">
              <div className="w-72 border-t border-slate-400 pt-2">
                <p className="font-semibold text-slate-800">
                  {veterinario.titulo ? `${veterinario.titulo} ` : ""}
                  {veterinario.nombre}
                </p>
                {veterinario.cedula && <p className="text-xs text-slate-500">Cédula profesional: {veterinario.cedula}</p>}
                {(veterinario.telefono || veterinario.email) && (
                  <p className="text-xs text-slate-400">{[veterinario.telefono, veterinario.email].filter(Boolean).join(" · ")}</p>
                )}
                <p className="mt-1 text-xs text-slate-400">Firma y sello</p>
              </div>
            </div>
          </section>

          <p className="mt-10 text-center text-[11px] text-slate-300">
            Documento emitido por {negocio.nombre} el {new Date(certificado.fechaEmision).toLocaleDateString("es-MX", { dateStyle: "long" })}.
            Este certificado refleja el estado de salud observado al momento de la revisión clínica.
          </p>
        </div>
      </div>
    </div>
  );
}
